import { Suspense, useMemo } from 'react'
import { Link } from 'react-router-dom'

import { paths } from 'src/config/paths'
import ProfilePhoto from 'src/shared/assets/images/profile-avatar-fallback.png'
import { Button } from 'src/shared/components/ui/buttons/button'
import { useWindowSize } from 'src/shared/hooks/use-window-size'

import type { PublicSearchUserData } from '../../api/marketplace/use-public-search'
import { useUserReviews } from '../../api/marketplace/use-user-reviews'
import { FilledCrown } from '../../assets/filled-crown'
import { FilledMapPin } from '../../assets/filled-map-pin'
import { ReviewStar } from '../../assets/review-star'
import type { Expertise } from '../../schemas/expertise'
import { expertiseMap } from '../../schemas/expertise'
import type { Profession } from '../../schemas/profession'
import { professionMap } from '../../schemas/profession'

import PublicUserCardSkeleton from './public-user-card-skeleton'

type PublicUserCardProps = {
  user: PublicSearchUserData
  isWithin: boolean
  isNearby?: boolean
}

const ReviewsContent = ({ userId }: { userId: number }) => {
  const { data: reviews } = useUserReviews(userId)

  if (
    !reviews ||
    !reviews.averageRating ||
    reviews.averageRating === 0 ||
    reviews.reviews.length === 0 ||
    reviews.totalReviews === 0
  ) {
    return null
  }

  const averageRating = reviews?.averageRating.toFixed(1)

  return (
    <div className="absolute bottom-1.5 left-1/2 flex -translate-x-1/2 flex-row items-center justify-center gap-1 rounded-[6px] bg-primary px-1.5 py-1 md:bottom-auto md:left-3.5 md:top-3.5 md:translate-x-0 md:px-2.5 md:py-1.5">
      <p className="text-[12px] font-bold leading-[100%] text-white md:text-[21px]">{averageRating}</p>
      <ReviewStar className="size-[9.5px] md:size-[14px]" />
    </div>
  )
}

const Reviews = ({ userId }: { userId: number }) => {
  return (
    <Suspense fallback={null}>
      <ReviewsContent userId={userId} />
    </Suspense>
  )
}

const PublicUserCardContent = ({ user }: PublicUserCardProps) => {
  const { width } = useWindowSize()

  const profilePhoto = useMemo(() => {
    if (user.profilePhoto) {
      return user.profilePhoto
    }

    return ProfilePhoto
  }, [user.profilePhoto])

  const fullName = `${user.firstName} ${user.lastName}`

  const location = user?.shortAddress || null
  const expertise =
    user?.expertise && Array.isArray(user.expertise) && user.expertise.length > 0
      ? expertiseMap[user.profession as Profession][user.expertise[0] as Expertise]?.label
      : null

  const includeSeparator = location && expertise

  if (width < 768) {
    return (
      <Link
        to={paths.users.profile.getHref(user.username)}
        className="flex w-full min-w-[280px] rounded-[25px] border-[0.5px] border-border bg-white"
        style={{
          boxShadow: '0px 1px 12px 1px rgba(27, 48, 41, 0.06)',
        }}
        target="_blank"
      >
        <div className="flex w-full flex-row items-center gap-[15px]">
          <div className="relative flex min-h-[100px] min-w-[100px] flex-row rounded-[25px] bg-border">
            {/* <ProfileAvatar size={100} userId={user.id} className="rounded-[25px]" /> */}
            <img
              src={profilePhoto}
              alt={fullName}
              className="size-[100px] shrink-0 rounded-[25px] bg-accent object-cover"
            />
            <Reviews userId={user.id} />
          </div>
          <div className="flex w-full flex-col gap-2 py-3">
            <div className="flex flex-col">
              <p className="inline-flex flex-wrap items-center break-all text-base font-bold">
                <span className="mr-1 break-all">{fullName}</span>
              </p>
              <p className="text-[13px] text-muted">{professionMap[user.profession].label}</p>
            </div>
            <div className="line-clamp-1 w-full break-all pr-4 text-xs font-semibold text-primary xs:text-sm">
              {location && (
                <span>
                  <FilledMapPin className="inline pb-1 pr-1 text-primary" size={19} />
                  {location}
                </span>
              )}
              {includeSeparator && (
                <span className="inline-block translate-y-[-2px] px-[5px] text-[10px] text-muted">•</span>
              )}
              {expertise && (
                <span>
                  <FilledCrown className="inline pb-1 pr-1 text-primary" size={19} />
                  {expertise}
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>
    )
  }

  return (
    <Link
      to={paths.users.profile.getHref(user.username)}
      className="border-primary/10 group relative flex size-full flex-col justify-between rounded-[25px] border shadow-[2px_2px_10px_0px_rgba(25,58,45,0.10)] transition-shadow duration-100 hover:shadow-[2px_2px_10px_0px_rgba(25,58,45,0.20)]"
      target="_blank"
    >
      <div>
        <div className="relative aspect-[30/26] w-full overflow-hidden rounded-[25px]">
          <img src={profilePhoto} alt={fullName} className="absolute inset-0 size-full object-cover" />
          <Reviews userId={user.id} />
        </div>
        <div className="p-5 text-center">
          <div className="flex flex-col gap-0.5">
            <h1 className="m-auto inline-flex flex-wrap items-center break-all text-2xl font-bold leading-[1.15]">
              <span className="mr-1 break-all">{fullName}</span>
            </h1>

            <div className="mb-3 flex items-center gap-2 text-center">
              <p className="m-auto text-base font-medium text-muted">{professionMap[user.profession].label}</p>
            </div>
            <div className="text-sm font-semibold text-primary">
              {location && (
                <span>
                  <FilledMapPin className="inline pb-1 pr-1 text-primary" size={19} />
                  {location}
                </span>
              )}
              {includeSeparator && (
                <span className="inline-block translate-y-[-2px] px-[5px] text-[10px] text-muted">•</span>
              )}
              {expertise && (
                <span>
                  <FilledCrown className="inline pb-1 pr-1 text-primary" size={19} />
                  {expertise}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="p-5 pt-0">
        <Button
          variant="default"
          size="lg"
          className="h-[50px] w-full rounded-full text-[15px] group-hover:bg-secondary group-hover:font-bold"
        >
          View profile
        </Button>
      </div>
    </Link>
  )
}

const PublicUserCard = ({ user, isWithin, isNearby = false }: PublicUserCardProps) => {
  return (
    <Suspense fallback={<PublicUserCardSkeleton />}>
      <PublicUserCardContent user={user} isWithin={isWithin} isNearby={isNearby} />
    </Suspense>
  )
}

export default PublicUserCard
